import { IOrder, IOrderItem } from '../../../types/order'
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer'
import { ImageLinks } from '../../../constants/image-link'
import SarabunFont from '../../../assets/fonts/th-sarabun.ttf'
import JsBarcode from 'jsbarcode'
import { numberFormat } from '../../../utils'

Font.register({ family: 'Sarabun', src: SarabunFont })

interface IPDFThaiPostProps {
  orders: IOrder[]
  width: number
  height: number
  showItemInPack?: boolean
  showItemAsTable?: boolean
}

const borderColor = '#333'
const defaultFontSize = 12
const tableStyles = StyleSheet.create({
  header: {
    padding: 3,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: defaultFontSize,
  },
  cell: { padding: 3, fontSize: defaultFontSize },
  cellLeft: { padding: 3, fontSize: defaultFontSize, textAlign: 'left' },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  headerRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  cellBorder: {
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  cellBorderFirst: {},
})

function BarCode({ value }: { value: string }) {
  const canvas = document.createElement('canvas')
  JsBarcode(canvas, value, {
    format: 'CODE39',
    displayValue: false,
    height: 80,
    width: 3,
    margin: 10,
  })
  const barcode = canvas.toDataURL()
  return <Image src={barcode} />
}

export const PDFThaiPost = (props: IPDFThaiPostProps) => {
  const { orders, width, height, showItemInPack, showItemAsTable } = props
  return (
    <Document>
      {orders.map((order: IOrder, index: number) => (
        <Page
          size={[width * 72, height * 72]}
          key={index}
          style={{
            flexDirection: 'row',
            fontFamily: 'Sarabun',
            fontWeight: 'bold',
          }}
        >
          <View style={{ flex: 1, padding: 10 }}>
            <Text
              style={{ fontSize: 14, fontWeight: 'bold', textAlign: 'center' }}
            >
              {order.trackingCode}{' '}
              {order.paymentMethod === 'COD' ? `(COD: ${order.total})` : ''}
            </Text>
            <View style={{ alignItems: 'center' }}>
              <BarCode value={order.trackingCode!} />
            </View>
            <Text
              style={{ fontSize: 13, fontWeight: 'bold', textAlign: 'center' }}
            >
              ORDER: {order.code}
            </Text>
            <Text
              style={{
                fontSize: defaultFontSize,
                marginTop: 8,
              }}
            >
              <Text style={{ fontWeight: 'bold' }}>ผู้ส่ง:</Text>{' '}
              {order.sender.name}
            </Text>
            <View style={{ marginTop: 8 }}>
              <Text
                style={{
                  fontSize: defaultFontSize,
                  lineHeight: 1,
                  marginBottom: 5,
                }}
              >
                <Text style={{ fontWeight: 'bold' }}>ผู้รับ:</Text>{' '}
                {order.recipient.name} {order.recipient.address} ต.
                {order.recipient.subDistrict} อำเภอ/เขต{' '}
                {order.recipient.district} จังหวัด {order.recipient.province}{' '}
                {order.recipient.zipcode}
              </Text>
              <Text
                style={{
                  fontSize: 16,
                  lineHeight: 1,
                  fontWeight: 'bold',
                }}
              >
                <Text style={{ fontWeight: 'bold' }}>โทร:</Text>{' '}
                {order.recipient.phone}
              </Text>
            </View>
            <View style={{ marginTop: 5 }}>
              <Text style={{ fontSize: defaultFontSize }}>
                <Text style={{ fontWeight: 'bold' }}>หมายเหตุ:</Text>{' '}
                {order.note ?? ''}
                {!showItemAsTable && (
                  <>
                    {' '}
                    {order.items
                      .map((item: IOrderItem) => {
                        let message = `${item.productName} ${item.quantity} ${
                          item.isCollection ? 'เซต' : 'ชิ้น'
                        } ${item?.meta?.size ? `ขนาด ${item.meta.size}` : ''} ${
                          item?.meta?.color ? `สี${item.meta.color}` : ''
                        } `
                        if (
                          item.isCollection &&
                          item.collections?.length &&
                          showItemInPack
                        ) {
                          message += '['
                          item.collections?.map((collection: any) => {
                            message += `${collection.productName} ${collection.quantity} ชิ้น,`
                          })
                          message += ']'
                        }
                        return message
                      })
                      .join(', ')}
                  </>
                )}
              </Text>
              {showItemAsTable && (
                <View style={{ marginTop: 4 }}>
                  <View style={tableStyles.headerRow}>
                    <Text
                      style={[
                        tableStyles.header,
                        tableStyles.cellBorderFirst,
                        { width: '10%' },
                      ]}
                    >
                      ลำดับ
                    </Text>
                    <Text
                      style={[
                        tableStyles.header,
                        tableStyles.cellBorder,
                        { width: '45%' },
                      ]}
                    >
                      รายการ
                    </Text>
                    <Text
                      style={[
                        tableStyles.header,
                        tableStyles.cellBorder,
                        { width: '15%' },
                      ]}
                    >
                      ยอดรวม
                    </Text>
                    <Text
                      style={[
                        tableStyles.header,
                        tableStyles.cellBorder,
                        { width: '15%' },
                      ]}
                    >
                      รหัสสินค้า
                    </Text>
                    <Text
                      style={[
                        tableStyles.header,
                        tableStyles.cellBorder,
                        { width: '15%' },
                      ]}
                    >
                      จำนวน
                    </Text>
                  </View>
                  {order.items.map((item: IOrderItem, itemIndex: number) => (
                    <View key={`item-${itemIndex}`}>
                      <View style={tableStyles.row}>
                        <Text
                          style={[
                            tableStyles.cell,
                            tableStyles.cellBorderFirst,
                            { width: '10%' },
                          ]}
                        >
                          {itemIndex + 1}
                        </Text>
                        <View
                          style={[
                            tableStyles.cellLeft,
                            tableStyles.cellBorder,
                            { width: '45%' },
                          ]}
                        >
                          <Text style={{ fontWeight: 'bold' }}>
                            {item.productName}
                          </Text>
                        </View>
                        <Text
                          style={[
                            tableStyles.cell,
                            tableStyles.cellBorder,
                            { width: '15%' },
                          ]}
                        >
                          {numberFormat(item.total)}
                        </Text>
                        <Text
                          style={[
                            tableStyles.cell,
                            tableStyles.cellBorder,
                            { width: '15%' },
                          ]}
                        >
                          {item.productCode}
                        </Text>
                        <Text
                          style={[
                            tableStyles.cell,
                            tableStyles.cellBorder,
                            { width: '15%' },
                          ]}
                        >
                          {item.quantity}
                        </Text>
                      </View>
                      {showItemInPack &&
                        item.isCollection &&
                        item.collections?.map((collection: any, collectionIndex: number) => (
                          <View
                            key={`product-coll-${itemIndex}-${collectionIndex}`}
                            style={tableStyles.row}
                          >
                            <Text
                              style={[
                                tableStyles.cell,
                                tableStyles.cellBorderFirst,
                                { width: '10%' },
                              ]}
                            >
                              {''}
                            </Text>
                            <View
                              style={[
                                tableStyles.cellLeft,
                                tableStyles.cellBorder,
                                { width: '60%' },
                              ]}
                            >
                              <Text style={{ paddingLeft: 5 }}>
                                {'- '}
                                {collection.productName}
                              </Text>
                            </View>
                            <Text
                              style={[
                                tableStyles.cell,
                                tableStyles.cellBorder,
                                { width: '15%' },
                              ]}
                            >
                              {collection.productCode}
                            </Text>
                            <Text
                              style={[
                                tableStyles.cell,
                                tableStyles.cellBorder,
                                { width: '15%' },
                              ]}
                            >
                              {numberFormat(collection.quantity)}
                            </Text>
                          </View>
                        ))}
                    </View>
                  ))}
                </View>
              )}
            </View>
            <View style={{ alignItems: 'center', marginTop: 8 }}>
              <Image
                src={ImageLinks.LOGO_THAILANDPOST}
                style={{ height: 'auto', width: 90 }}
              />
            </View>
          </View>
        </Page>
      ))}
    </Document>
  )
}
